import React from 'react';
import { ErrorMessage, FlexRow, Grid, Header3Text } from 'ui-toolkit';
import { Field, FieldArray, FieldProps } from 'formik';
import { BlockRedirection } from 'components/cdBuilder/BlockRedirection';
import { CreateEditInternalTransferRow } from './CreateEditInternalTransferRow';
import { IInternalTransferData } from 'types/order/escrow/IInternalTransferData';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { HasPermissionButton } from '../../shared/HasPermissionButton';
import { CheckForAvailableFundsModal } from '../../escrowCreateEdit/checkForAvailableFundsModal/CheckForAvailableFundsModal';
import { InternalTransfersLoadingContainer } from './containers/InternalTransfersLoadingContainer';
import { useHasPermissions } from 'components/accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { useCreateEditInternalTransfer } from '../../hooks/useCreateEditInternalTransfer';

export interface ICreateEditInternalTransfersContainerProps {
  orderId: string;
  dirty: boolean;
  onTransactionUpdate: () => void;
  availableAmountDollars: number;
}

export const CreateEditInternalTransfersContainer: React.FC<ICreateEditInternalTransfersContainerProps> = ({
  orderId,
  dirty,
  onTransactionUpdate,
  availableAmountDollars,
}) => {
  const hasEditPermission = useHasPermissions([ePermissions.EscrowExpectedInternalTransfersEdit]);
  const { data: order } = useSelectCurrentOrder();
  const sprucePowered = order?.client.sprucePowered || false;

  const {
    showWarningModal,
    setShowWarningModal,
    sendInternalTransferId,
    sendFetchStatus,
    sendFetchError,
    updateFetchStatus,
    getDestinationAccountsForState,
    onRemoveInternalTransfer,
    onSendInternalTransfer,
  } = useCreateEditInternalTransfer({
    orderId,
    availableAmountDollars,
    onTransactionUpdate,
  });

  return (
    <>
      <ErrorMessage error={sendFetchError} />
      <BlockRedirection showSaveSpinner={false} showSaveSuccess={false} isDirty={dirty} />
      <FieldArray name={'restrictedInternalTransfers'}>
        {({ push, remove }) => (
          <>
            <Grid>
              <FlexRow>
                <Header3Text>Internal Transfers</Header3Text>
                <HasPermissionButton
                  hasEditPermission={hasEditPermission}
                  push={push}
                  successMessage={'Add Internal Transfer'}
                  transferType={'internal transfers'}
                />
              </FlexRow>
              <InternalTransfersLoadingContainer orderId={orderId} isSprucePowered={sprucePowered} />
              <Field name={'restrictedInternalTransfers'}>
                {({ field }: FieldProps<IInternalTransferData[]>) => (
                  <>
                    {(field.value || []).map((internalTransfer, index) => (
                      <>
                        <CreateEditInternalTransferRow
                          key={index}
                          index={index}
                          onDelete={async (index) => {
                            remove(index);
                            await onRemoveInternalTransfer(internalTransfer);
                          }}
                          fieldName={'restrictedInternalTransfers'}
                          transferData={internalTransfer}
                          onSend={() => onSendInternalTransfer(internalTransfer)}
                          parties={order.parties}
                          state={order.property.state}
                          fetching={
                            (sendFetchStatus.fetching || updateFetchStatus.fetching) && !!sendInternalTransferId
                          }
                          destinationAccounts={getDestinationAccountsForState?.data}
                        />
                        {showWarningModal && (
                          <CheckForAvailableFundsModal
                            submitData={() => onSendInternalTransfer(internalTransfer)}
                            type="internalTransfer"
                            handleClose={() => setShowWarningModal(false)}
                          />
                        )}
                      </>
                    ))}
                  </>
                )}
              </Field>
            </Grid>
          </>
        )}
      </FieldArray>
    </>
  );
};
